/**
 * What a Find search costs, in the panel's words. Dollars for now; all formatting goes through
 * `formatCost`, so cost can become credits in one place.
 */

/** "$0.42", or "< $0.01" for less than a cent; null when there is no amount. */
export const formatCost = usd => {
	const amount = Number(usd);
	if (usd === null || usd === undefined || !Number.isFinite(amount)) return null;
	if (amount < 0.01) return '< $0.01';
	return `$${amount.toFixed(2)}`;
};

/** "≈ 1 min", "≈ 12 min", or "< 1 min": how long a search should take. */
export const formatDuration = seconds => {
	const s = Number(seconds);
	if (!Number.isFinite(s) || s <= 0) return null;
	if (s < 45) return '< 1 min';
	return `≈ ${Math.max(1, Math.round(s / 60))} min`;
};

/**
 * The body of `POST /find/estimate` for the search the Find button would run, or null when it
 * would run nothing yet (no area, no words, no examples, no model). Same fields as the search's own
 * body, so the estimate is of the same squares.
 * @param {Object} next
 * @param {'query'|'segment'|'similar'|'model'|'rest'} next.kind
 * @param {number[]} next.bounds - EPSG:3857
 * @param {number[][]} [next.polygon] - A drawn area's ring
 * @param {number} [next.maxTiles]
 * @param {string[]} [next.objects] - Words (query, segment: roads, rivers… traced, not boxed)
 * @param {{ boxes: number[][] }[]} [next.groups] - Examples per object (similar, rest)
 * @param {string} [next.modelUuid] - Model
 * @param {boolean} [next.verify]
 */
export const estimateBody = ({
	kind,
	bounds,
	polygon,
	maxTiles,
	objects,
	groups,
	modelUuid,
	verify,
}) => {
	if (!kind || !bounds?.length) return null;
	const words = (objects ?? []).map(word => word.trim()).filter(Boolean);
	const marked = (groups ?? []).filter(group => group.boxes?.length);
	const worded = kind === 'query' || kind === 'segment';
	if (worded && !words.length) return null;
	if ((kind === 'similar' || kind === 'rest') && !marked.length) return null;
	if (kind === 'model' && !modelUuid) return null;
	return {
		kind,
		bounds,
		...(polygon?.length && { polygon }),
		...(maxTiles && { max_tiles: maxTiles }),
		...(worded && { objects: words }),
		...((kind === 'similar' || kind === 'rest') && {
			groups: marked.map(({ boxes }) => ({ boxes })),
		}),
		...(kind === 'model' && { model_uuid: modelUuid }),
		...(verify !== undefined && { verify }),
	};
};

/** A run's cost (its `cost`, as "$0.21" in "803 trees · $0.21"); null when unknown. */
export const runCost = cost => formatCost(cost?.usd_total);
